import { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface CalendarProps {
  highlightedDates?: Date[];
  selectedDate?: Date | null;
  onSelectDate?: (date: Date | null) => void;
}

const DAYS = ['H', 'K', 'Sze', 'Cs', 'P', 'Szo', 'V'];
const MONTHS = [
  'Január', 'Február', 'Március', 'Április', 'Május', 'Június',
  'Július', 'Augusztus', 'Szeptember', 'Október', 'November', 'December'
];

export const Calendar = ({ highlightedDates = [], selectedDate, onSelectDate }: CalendarProps) => {
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  const highlightedSet = useMemo(() => {
    const set = new Set<string>();
    highlightedDates.forEach(date => {
      set.add(`${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`);
    });
    return set;
  }, [highlightedDates]);

  const calendarDays = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    
    // First day of month (0 = Sunday, we need Monday = 0)
    const firstDay = new Date(year, month, 1);
    let startDay = firstDay.getDay() - 1;
    if (startDay < 0) startDay = 6; // Sunday becomes 6
    
    // Days in month
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    
    // Days from previous month
    const prevMonthDays = new Date(year, month, 0).getDate();
    
    const days: { date: Date; isCurrentMonth: boolean; isHighlighted: boolean; isSelected: boolean; isToday: boolean }[] = [];
    
    // Previous month days
    for (let i = startDay - 1; i >= 0; i--) {
      const date = new Date(year, month - 1, prevMonthDays - i);
      days.push({
        date,
        isCurrentMonth: false,
        isHighlighted: highlightedSet.has(`${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`),
        isSelected: selectedDate ? date.toDateString() === selectedDate.toDateString() : false,
        isToday: date.toDateString() === new Date().toDateString(),
      });
    }
    
    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(year, month, i);
      days.push({
        date,
        isCurrentMonth: true,
        isHighlighted: highlightedSet.has(`${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`),
        isSelected: selectedDate ? date.toDateString() === selectedDate.toDateString() : false,
        isToday: date.toDateString() === new Date().toDateString(),
      });
    }
    
    // Next month days (fill to complete the grid)
    const remainingDays = 42 - days.length; // 6 rows * 7 days
    for (let i = 1; i <= remainingDays; i++) {
      const date = new Date(year, month + 1, i);
      days.push({
        date,
        isCurrentMonth: false,
        isHighlighted: highlightedSet.has(`${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`),
        isSelected: selectedDate ? date.toDateString() === selectedDate.toDateString() : false,
        isToday: date.toDateString() === new Date().toDateString(),
      });
    }
    
    return days;
  }, [currentMonth, highlightedSet, selectedDate]);

  const goToPrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const goToToday = () => {
    const now = new Date();
    setCurrentMonth(new Date(now.getFullYear(), now.getMonth(), 1));
    onSelectDate?.(null);
  };

  const handleDayClick = (day: typeof calendarDays[0]) => {
    if (day.isSelected) {
      onSelectDate?.(null);
    } else {
      onSelectDate?.(day.date);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3 sm:p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <button
          onClick={goToPrevMonth}
          className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
        >
          <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 dark:text-gray-400" />
        </button>
        
        <div className="flex items-center gap-2">
          <span className="font-semibold text-gray-900 dark:text-gray-100 text-sm sm:text-base">
            {MONTHS[currentMonth.getMonth()]} {currentMonth.getFullYear()}
          </span>
          <button
            onClick={goToToday}
            className="px-2 py-0.5 text-xs bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-300 rounded transition-colors"
          >
            Ma
          </button>
        </div>
        
        <button
          onClick={goToNextMonth}
          className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
        >
          <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 dark:text-gray-400" />
        </button>
      </div>

      {/* Day headers */}
      <div className="grid grid-cols-7 gap-1 mb-1">
        {DAYS.map((day) => (
          <div key={day} className="text-center text-xs font-medium text-gray-500 dark:text-gray-400 py-1">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-1">
        {calendarDays.map((day, index) => (
          <button
            key={index}
            onClick={() => handleDayClick(day)}
            className={`
              relative aspect-square flex items-center justify-center text-xs sm:text-sm rounded-lg transition-colors
              ${day.isCurrentMonth ? 'text-gray-900 dark:text-gray-100' : 'text-gray-400 dark:text-gray-500'}
              ${day.isSelected ? 'bg-primary-600 text-white font-semibold' : ''}
              ${day.isToday && !day.isSelected ? 'ring-2 ring-primary-500 ring-inset' : ''}
              ${!day.isSelected ? 'hover:bg-gray-100 dark:hover:bg-gray-700' : ''}
            `}
          >
            {day.date.getDate()}
            {day.isHighlighted && (
              <span className={`absolute bottom-0.5 left-1/2 -translate-x-1/2 w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full ${day.isSelected ? 'bg-white' : 'bg-primary-500'}`} />
            )}
          </button>
        ))}
      </div>

      {/* Legend */}
      <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700 flex flex-wrap items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
        <div className="flex items-center gap-1.5">
          <span className="w-2 h-2 sm:w-2.5 sm:h-2.5 bg-primary-500 rounded-full" />
          <span>Edzés nap</span>
        </div>
        {selectedDate && (
          <button
            onClick={() => onSelectDate?.(null)}
            className="text-primary-600 hover:underline"
          >
            Szűrés törlése
          </button>
        )}
      </div>
    </div>
  );
};
